import { fetchClientInstanceId } from '@/utils/clientInstance';

/**
 * Channel name for the cross-tab auth BroadcastChannel.
 * Kept separate from the 'activity' idle channel to preserve clean separation of concerns.
 */
const CHANNEL_NAME = 'cxme-auth';

/**
 * Set before a remote-logout navigation so /login does not bounce back to home
 * when userStatus still briefly reports authenticated (cookie race).
 */
export const FORCE_LOGGED_OUT_KEY = 'cxme-force-logged-out';

/**
 * Unique tab identifier — re-uses the in-memory clientInstanceId so originating
 * tab can be excluded from its own broadcast responses.
 */
const TAB_ID = fetchClientInstanceId();

// ─── Message types ──────────────────────────────────────────────────────────

export type CrossTabAuthMessageType =
    | 'logout'
    | 'session-invalidated'
    | 'login-in-progress'
    | 'login-ended'
    | 'login-success'
    | 'csrf-update'; // retained for compat; no longer emitted (cross-tab apply caused 401s)

export interface CrossTabAuthMessage {
    type: CrossTabAuthMessageType;
    originTabId: string;
    /** For 'csrf-update' messages — the new CSRF token. */
    token?: string;
}

// ─── Singleton channel ───────────────────────────────────────────────────────

let _channel: BroadcastChannel | null = null;
type Handler = (msg: CrossTabAuthMessage) => void;
const _handlers = new Map<CrossTabAuthMessageType, Set<Handler>>();

/** Pending delayed reload from login-success — must be cancellable on logout. */
let _pendingLoginReloadTimer: number | null = null;

function getChannel(): BroadcastChannel | null {
    if (typeof BroadcastChannel === 'undefined') return null;
    if (!_channel) {
        _channel = new BroadcastChannel(CHANNEL_NAME);
        _channel.onmessage = (event: MessageEvent<CrossTabAuthMessage>) => {
            const msg: CrossTabAuthMessage = event.data;
            // Ignore messages that originated in this same tab
            if (!msg || msg.originTabId === TAB_ID) return;
            const handlers = _handlers.get(msg.type);
            if (handlers) {
                handlers.forEach((h) => h(msg));
            }
        };
    }
    return _channel;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Broadcast an auth event to all other open tabs.
 *
 * @param type  The event type.
 * @param token Optional CSRF token (for 'csrf-update' messages).
 */
export function broadcastAuthEvent(type: CrossTabAuthMessageType, token?: string): void {
    const channel = getChannel();
    if (!channel) return;
    const msg: CrossTabAuthMessage = { type, originTabId: TAB_ID };
    if (typeof token !== 'undefined') msg.token = token;
    try {
        channel.postMessage(msg);
    } catch (err) {
        console.warn('[useCrossTabAuth] Failed to broadcast:', type, err);
    }
}

/**
 * Register a handler for a specific auth event type.
 * Returns a cleanup function that removes the handler.
 *
 * Call this once in App.vue (globally mounted component) — not inside
 * components that mount/unmount repeatedly — so the listener persists for the
 * full page lifetime.
 */
export function onAuthMessage(
    type: CrossTabAuthMessageType,
    handler: Handler,
): () => void {
    getChannel(); // ensure channel is initialised
    if (!_handlers.has(type)) {
        _handlers.set(type, new Set());
    }
    _handlers.get(type)!.add(handler);

    return () => {
        _handlers.get(type)?.delete(handler);
    };
}

/**
 * Schedule a delayed full-page reload for cross-tab login sync.
 * Replaces any previously scheduled reload in this tab.
 */
export function scheduleLoginSuccessReload(delayMs: number): void {
    cancelLoginSuccessReload();
    _pendingLoginReloadTimer = window.setTimeout(() => {
        _pendingLoginReloadTimer = null;
        window.location.reload();
    }, delayMs);
}

/**
 * Cancel a pending login-success reload (e.g. when a logout arrives first).
 */
export function cancelLoginSuccessReload(): void {
    if (_pendingLoginReloadTimer !== null) {
        clearTimeout(_pendingLoginReloadTimer);
        _pendingLoginReloadTimer = null;
    }
}

export function markForceLoggedOut(): void {
    try {
        sessionStorage.setItem(FORCE_LOGGED_OUT_KEY, '1');
        sessionStorage.removeItem('cxme-cross-tab-login-reload');
        sessionStorage.removeItem('cxme-pending-csrf');
    } catch { /* ignore */ }
}

export function consumeForceLoggedOut(): boolean {
    try {
        if (sessionStorage.getItem(FORCE_LOGGED_OUT_KEY) === '1') {
            sessionStorage.removeItem(FORCE_LOGGED_OUT_KEY);
            return true;
        }
    } catch { /* ignore */ }
    return false;
}

/**
 * Tear down the BroadcastChannel (called on page unload if needed, or in tests).
 */
export function closeCrossTabAuthChannel(): void {
    cancelLoginSuccessReload();
    if (_channel) {
        _channel.close();
        _channel = null;
    }
    _handlers.clear();
}
